<template>
  <div class="event-date-form">
    <div class="event-date-form__section">
      <BaseTextInput
        name="event-start-datetime"
        type="datetime-local"
        :value="toInputValue(formData.start_datetime)"
        :label="$t('START')"
        :error-message="fieldErrors.startDateTime"
        @change="
          (value) => {
            formData.start_datetime = value
            setEndDateAnHourLater()
            validateStartDateTime()
            validateEndDateTime()
            validateDeadlineDate()
          }
        "
      />

      <BaseTextInput
        name="event-end-datetime"
        type="datetime-local"
        :value="toInputValue(formData.end_datetime)"
        :label="$t('END')"
        :error-message="fieldErrors.endDateTime"
        @change="
          (value) => {
            formData.end_datetime = value
            validateEndDateTime()
          }
        "
      />

      <BaseTextInput
        name="event-signup-deadline-datetime"
        type="datetime-local"
        optional
        :value="toInputValue(formData.signup_deadline)"
        :label="$t('product_events.DEADLINE')"
        :error-message="fieldErrors.deadlineDateTime"
        @change="
          (value) => {
            formData.signup_deadline = value
            validateDeadlineDate()
          }
        "
      />

      <div class="event-date-form__timezone">
        <label class="event-date-form__timezone-label" for="event-timezone">
          {{ $t('product_events.TIMEZONE') }}
        </label>
        <div class="event-date-form__timezone-control">
          <BaseSelect
            id="event-timezone"
            v-model="formData.time_zone"
            data-cy="date-timezone"
          >
            <BaseOption
              v-for="option in timezoneOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </BaseOption>
          </BaseSelect>
          <HelpTooltip
            ><template #description>{{
              $t('product_events.TIMEZONE_HELP')
            }}</template></HelpTooltip
          >
        </div>
      </div>
    </div>

    <!-- Location: -->
    <div class="event-date-form__section">
      <InstructorFormElement
        :errors="fieldErrors.instructor"
        :instructor-name="formData.instructor"
        @changeInstructor="
          (instructor) => {
            formData.instructor = instructor.instructorName
            formData.instructor_record = { id: instructor.instructorId }
            validateInstructor()
          }
        "
      />

      <BaseTextInput
        :value="formData.location"
        :placeholder="$t('product_events.ADDRESS_PLACEHOLDER')"
        name="address"
        :label="$t('product_events.ADDRESS')"
        :error-message="fieldErrors.address"
        :data-cy="'date-address'"
        @change="
          (value) => {
            formData.location = value
            validateLocation()
          }
        "
      />

      <div class="event-date-form__row">
        <BaseTextInput
          :value="String(formData.attendees_count_max)"
          :label="$t('product_events.CAPACITY')"
          :placeholder="$t('product_events.CAPACITY_PLACEHOLDER')"
          class="flex-1"
          type="number"
          name="capacity"
          :error-message="fieldErrors.capacity"
          :min="minCapacity"
          :max="maxCapacity"
          :data-cy="'date-capacity'"
          @change="
            (value) => {
              formData.attendees_count_max = value
              validateMaxCapacity()
            }
          "
        />

        <BaseTextInput
          v-model="formData.room"
          class="flex-1"
          :label="$t('product_events.ROOM')"
          :placeholder="$t('product_events.ROOM_PLACEHOLDER')"
          :optional="true"
          name="room"
          :data-cy="'date-room'"
        />
      </div>
    </div>

    <div class="event-date-form__section">
      <BaseTextInput
        :value="formData.meeting_link"
        :label="$t('product_events.ONLINE_MEETING')"
        :name="$t('product_events.ONLINE_MEETING')"
        :placeholder="$t('product_events.ONLINE_MEETING_LINK')"
        :optional="true"
        :error-message="fieldErrors.meetingLink"
        type="url"
        @change="
          (value) => {
            formData.meeting_link = value
            validateMeetingLink()
          }
        "
      />

      <BaseTextInput
        :value="formData.course_material"
        :label="$t('product_events.COURSE_MATERIAL')"
        :name="$t('product_events.COURSE_MATERIAL')"
        :placeholder="$t('product_events.COURSE_MATERIAL_LINK')"
        :optional="true"
        :error-message="fieldErrors.courseMaterial"
        type="url"
        @change="
          (value) => {
            formData.course_material = value
            validateCourseMaterial()
          }
        "
      />

      <!--      TODO: text key for legend-->
      <BaseFieldset legend="Admission">
        <div class="event-date-form__radio-row">
          <BaseRadio
            :label="$t('AUTOMATIC')"
            value="automatic"
            name="admission-strategy"
            :selected-value="formData.admission_strategy"
            @onSelect="(value) => (formData.admission_strategy = value)"
          />
          <HelpTooltip
            ><template #description>{{
              $t('product_events.ADMISSION_STRATEGY_AUTOMATIC_DESCRIPTION')
            }}</template></HelpTooltip
          >
        </div>

        <div class="event-date-form__radio-row">
          <BaseRadio
            :label="$t('MANUAL')"
            value="manual"
            name="admission-strategy"
            :selected-value="formData.admission_strategy"
            @onSelect="(value) => (formData.admission_strategy = value)"
          />
          <HelpTooltip
            ><template #description>{{
              $t('product_events.ADMISSION_STRATEGY_MANUAL_DESCRIPTION')
            }}</template></HelpTooltip
          >
        </div>
      </BaseFieldset>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatNumberDigits } from '@/helpers/numbers.js'
import { computed, reactive, watch } from 'vue'
import InstructorFormElement from './InstructorFormElement.vue'
import { useI18n } from '@/hooks/useI18N.ts'
import BaseTextInput from '@/components/generics/base/BaseTextInput.vue'
import { useCompanyLocale } from '@/hooks/useCompanyLocale'
import BaseFieldset from '@/components/generics/base/BaseFieldset.vue'
import BaseRadio from '@/components/generics/base/BaseRadio.vue'
import HelpTooltip from '@/components/generics/HelpTooltip.vue'
import BaseSelect, {
  BaseOption,
} from '@/components/generics/base/BaseSelect.vue'
import {
  buildTimezoneOptions,
  getBrowserTimeZone,
  instantToZonedInputValue,
} from '@/helpers/timezones.ts'
import type { EventAdmissionStrategy } from '@/service/v3/endpoints/types/eventTypes.ts'

interface InstructorRecord {
  id: number
}

export interface EventFormValues {
  start_datetime: string
  end_datetime: string
  signup_deadline: string
  location: string
  room: string
  instructor: string
  instructor_record?: InstructorRecord
  attendees_count_max: number
  course_material: string
  meeting_link: string
  waiting_list_enabled: boolean
  admission_strategy: EventAdmissionStrategy
  time_zone: string
}

interface Props {
  values?: Partial<EventFormValues>
}

const props = withDefaults(defineProps<Props>(), {
  values: undefined,
})

const emit = defineEmits<{
  onSubmitSuccess: [formData: EventFormValues]
}>()

const i18n = useI18n()
const { companyLocaleCode } = useCompanyLocale()
const locale = computed(() => companyLocaleCode.value || 'en')
const minCapacity = 1
const maxCapacity = 99999

// The full IANA zone list is static for the lifetime of the form, so it is
// built once rather than per-render.
const timezoneOptions = buildTimezoneOptions()

const DEFAULT_EVENT_FORM_VALUES: EventFormValues = {
  attendees_count_max: 0,
  instructor: '',
  instructor_record: undefined,
  location: '',
  room: '',
  start_datetime: '',
  end_datetime: '',
  signup_deadline: '',
  course_material: '',
  meeting_link: '',
  // We're phasing this field out as the waiting list will exist at all times
  // Instead admission strategy will have full control of how users receive slots on the event
  waiting_list_enabled: true,
  admission_strategy: 'automatic',
  // Defaults to (and, for a null/blank API value, is coerced to) the browser
  // zone so the submit payload always carries a non-null `time_zone`. With the
  // backend interpreting naive datetimes in the course zone, a blank zone would
  // otherwise be parsed in the app default zone — a silent behaviour change for
  // legacy events. See the "null time_zone write contract" in the plan.
  time_zone: getBrowserTimeZone(),
}

/**
 * Merges provided values with defaults, filtering out null/undefined values
 * to ensure default values are always used for missing or invalid field values.
 * Note: This should be ideally be handled by a robust form library instead.
 */
const mergeWithDefaults = (
  values?: Partial<EventFormValues>
): EventFormValues => {
  if (!values) return { ...DEFAULT_EVENT_FORM_VALUES }

  const filteredValues = Object.entries(values).reduce(
    (acc, [key, value]) => {
      // Only include values that are not null or undefined
      if (value !== null && value !== undefined) {
        acc[key as keyof EventFormValues] = value
      }
      return acc
    },
    {} as Partial<EventFormValues>
  )

  const merged = {
    ...DEFAULT_EVENT_FORM_VALUES,
    ...filteredValues,
  }

  // Null/blank write contract: the backend interprets naive datetimes in the
  // course `time_zone`, so a blank zone would be parsed in the app default zone.
  // Coerce any blank value (a legacy event lacking a stored zone) to the browser
  // zone so the form always submits a concrete zone.
  if (!merged.time_zone) {
    merged.time_zone = getBrowserTimeZone()
  }

  return merged
}

const formData = reactive<EventFormValues>(mergeWithDefaults(props.values))

// Reactively update form if updated props are passed, ensuring defaults for null/undefined values
watch(
  () => props.values,
  (newValues) => {
    const mergedValues = mergeWithDefaults(newValues)
    Object.assign(formData, mergedValues)
  },
  { deep: true }
)

const fieldErrors = reactive({
  startDateTime: '',
  endDateTime: '',
  deadlineDateTime: '',
  address: '',
  instructor: '',
  capacity: '',
  meetingLink: '',
  courseMaterial: '',
})

const isFormValid = computed(() =>
  Object.values(fieldErrors).every((fieldError) => fieldError.length === 0)
)

const submit = () => {
  validateForm()

  if (isFormValid.value) {
    emit('onSubmitSuccess', formData)
  } else {
    console.error('Form is not valid')
  }
}

// Expose submit method to parent
defineExpose({
  submit,
})

// A `datetime-local` value the input just emitted: a bare wall-clock with no
// timezone designator. API instants always carry seconds and/or an offset/`Z`
// (ISO 8601 with timezone), so this never matches a value loaded from the API.
const NAIVE_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/

const isNaiveDateTime = (value: string): boolean =>
  NAIVE_DATETIME_PATTERN.test(value)

/** Format a `Date`'s local wall-clock as a naive `YYYY-MM-DDTHH:mm` string. */
const toNaiveDateTime = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const dateOfMonth = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())

  return `${year}-${month}-${dateOfMonth}T${hours}:${minutes}`
}

/**
 * Resolve the value shown in a `datetime-local` input.
 *
 * - An API instant (ISO 8601 with offset/`Z`) is rendered as its wall-clock in
 *   the selected `time_zone`, so the same instant re-renders when the zone
 *   changes.
 * - A value the user just typed is already a naive wall-clock in the selected
 *   zone, so it is returned unchanged (re-feeding it through the instant
 *   converter would reinterpret it in the browser zone and make the field jump).
 */
const toInputValue = (value: string): string => {
  if (!value) return ''
  if (isNaiveDateTime(value)) return value
  return instantToZonedInputValue(value, formData.time_zone)
}

/**
 * Parse a form datetime field into a `Date` that is comparable against the
 * other datetime fields.
 *
 * A field holds either an API instant (parsed by `new Date` as UTC) or a
 * freshly-typed naive value (parsed as browser-local), so comparing them
 * directly would mix reference frames — producing false "end before start" /
 * "deadline after start" errors for an admin whose browser zone differs from
 * the event zone. Projecting both through `toInputValue` first expresses each
 * as the selected zone's wall-clock; parsing those identically in the browser
 * zone cancels the offset, reducing the comparison to wall-clock ordering in
 * the event zone.
 */
const toComparableDate = (value: string): Date =>
  new Date(toInputValue(value))

const setEndDateAnHourLater = () => {
  if (!formData.start_datetime) return

  const startDate = toComparableDate(formData.start_datetime)
  const endDate = toComparableDate(formData.end_datetime)
  const isEndDateTimeSet = formData.end_datetime.length > 0

  if (!isEndDateTimeSet || endDate <= startDate) {
    const newEndDateTime = new Date(startDate.getTime() + 60 * 60 * 1000)
    // Emit a naive wall-clock; the backend derives the authoritative instant in
    // the course `time_zone`. The +1h delta is on the naive value (parse local,
    // reformat local — the offset cancels), so a DST boundary may skip/repeat an
    // hour, which the backend resolves.
    formData.end_datetime = toNaiveDateTime(newEndDateTime)
  }
}

const validateForm = () => {
  validateStartDateTime()
  validateEndDateTime()
  validateDeadlineDate()
  validateLocation()
  validateInstructor()
  validateMaxCapacity()
  validateMeetingLink()
  validateCourseMaterial()
}

// Validation functions
const validateStartDateTime = () => {
  const startDateTime = formData.start_datetime
  if (!startDateTime) {
    fieldErrors.startDateTime = i18n.t(
      'product_events.error_messages.INVALID_DATE'
    )
  } else {
    fieldErrors.startDateTime = ''
  }
}

const validateEndDateTime = () => {
  const endDateTime = formData.end_datetime
  const startDateObj = toComparableDate(formData.start_datetime)
  const endDateObj = toComparableDate(endDateTime)

  if (endDateTime.length === 0) {
    fieldErrors.endDateTime = i18n.t(
      'product_events.error_messages.INVALID_DATE'
    )
  } else if (endDateObj <= startDateObj) {
    fieldErrors.endDateTime = i18n.t(
      'product_events.error_messages.END_DATE_BEFORE'
    )
  } else {
    fieldErrors.endDateTime = ''
  }
}

const validateDeadlineDate = () => {
  const deadlineDate = formData.signup_deadline

  // Clear error if deadline is empty (it's optional)
  if (!deadlineDate || deadlineDate.length === 0) {
    fieldErrors.deadlineDateTime = ''
    return
  }

  const deadlineDateObj = toComparableDate(deadlineDate)
  const startDateObj = toComparableDate(formData.start_datetime)

  // Deadline must be before the event start date
  if (deadlineDateObj >= startDateObj) {
    fieldErrors.deadlineDateTime = i18n.t(
      'product_events.error_messages.DEADLINE_BEFORE_START'
    )
  } else {
    fieldErrors.deadlineDateTime = ''
  }
}

const validateLocation = () => {
  if (formData.location?.length === 0) {
    fieldErrors.address = i18n.t('error.CANNOT_BE_EMPTY')
  } else {
    fieldErrors.address = ''
  }
}

const validateInstructor = () => {
  if (formData.instructor?.length === 0) {
    fieldErrors.instructor = i18n.t('error.CANNOT_BE_EMPTY')
  } else {
    fieldErrors.instructor = ''
  }
}

const validateMaxCapacity = () => {
  const attendeesCountMax = Number(formData.attendees_count_max)
  if (attendeesCountMax > maxCapacity) {
    const lessThan = maxCapacity + 1
    const lessThanFormatted = formatNumberDigits(lessThan, locale.value, 6)
    fieldErrors.capacity = `${i18n.t(
      'error.MUST_BE_LESS_THAN'
    )} ${lessThanFormatted}`
  } else if (!attendeesCountMax || attendeesCountMax < minCapacity) {
    fieldErrors.capacity = `${i18n.t('error.MUST_BE_AT_LEAST')} ${minCapacity}`
  } else {
    fieldErrors.capacity = ''
  }
}

const validateMeetingLink = () =>
  (fieldErrors.meetingLink = validateURL(formData.meeting_link))

const validateCourseMaterial = () =>
  (fieldErrors.courseMaterial = validateURL(formData.course_material))

const validateURL = (urlAsString: string): string => {
  if (urlAsString.length === 0) return ''

  const validURLProtocols = ['https:', 'http:']
  try {
    const url = new URL(urlAsString)
    if (!validURLProtocols.includes(url.protocol)) {
      return i18n.t('urlErrors.INVALID_URL_PROTOCOL')
    }
  } catch (error) {
    return i18n.t('urlErrors.INVALID_URL')
  }
  return ''
}
</script>

<style lang="scss" scoped>
.event-date-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: $spacing-m;
  min-width: 0;
  max-width: 900px;

  &__section {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: $spacing-3xs;
  }

  &__row {
    display: flex;
    gap: $spacing-2xs;
  }

  &__timezone {
    display: flex;
    flex-direction: column;
  }

  &__timezone-label {
    @include formLabelStyle;
  }

  &__timezone-control {
    display: flex;
    align-items: center;
    gap: $spacing-3xs;
    min-width: 0;
  }
}

.flex-1 {
  flex: 1;
}

.event-date-form__radio-row {
  display: flex;
  align-items: center;
}
</style>
